import { type ChangeEvent, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import type { DecoratedOrder } from '@witmetrics/utils';
import { useActiveLab } from '@witmetrics/data-store';
import { isBlank, strip } from '@witmetrics/utils/strings';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useString } from '@/hooks/useString';
import { useArray } from '@/hooks/useArray';
import Button from '@/components/Button';
import SlideTransition from '@/components/SlideTransition';
import SearchResult from './SearchResult';
import { classes, fetchMatchingOrders, fetchRecentOrders } from './utils';

export type OrderSearchDialogProps = {
  onClose: () => void;
};

export default function OrderSearchDialog({ onClose }: OrderSearchDialogProps) {
  const timestampRef = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);
  const { onApiError, onSelectOrder, recentOrderIds, onClearRecentOrders } =
    useAppState();
  const activeLab = useActiveLab();
  const [search, setSearch] = useString();
  const [suggestions, setSuggestions] = useArray<DecoratedOrder>();
  const [recentOrders, setRecentOrders, { clear: clearRecentOrders }] =
    useArray<DecoratedOrder>();

  useEffect(() => {
    if (activeLab) handleFetchRecentOrders();
  }, [activeLab, recentOrderIds]);

  const handleFetchRecentOrders = async () => {
    if (isEmpty(recentOrderIds)) return clearRecentOrders();
    try {
      setRecentOrders(await fetchRecentOrders(activeLab!.id, recentOrderIds));
    } catch (err) {
      onApiError(err, 'Error fetching recent orders');
    }
  };

  const handleClearRecentOrders = () => {
    onClearRecentOrders();
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSelect = (orderId: number) => {
    onSelectOrder(orderId);
    onClose();
  };

  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    const newSearch = e.target.value;
    const query = strip(newSearch);
    let searchSuggestions: DecoratedOrder[] = [];
    setSearch(newSearch);
    if (!isBlank(query)) {
      try {
        const results = await fetchMatchingOrders(activeLab!.id, query);
        // Prevent overwriting newer responses with slower previous ones
        if (results.timestamp > timestampRef.current) {
          searchSuggestions = results.orders;
        } else {
          searchSuggestions = suggestions;
        }
        timestampRef.current = results.timestamp;
      } catch (err) {
        onApiError(err, 'Error finding orders');
      }
    }
    setSuggestions(searchSuggestions);
  };

  if (!activeLab) return null;

  const showRecent = isBlank(search) && !isEmpty(recentOrders);
  const orders = showRecent ? recentOrders : suggestions;

  return (
    <Dialog
      open
      keepMounted
      disableRestoreFocus
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      TransitionComponent={SlideTransition}
      onClose={onClose}>
      <div className={classes.wrapper}>
        <InputBase
          ref={inputRef}
          autoFocus
          className={classes.inputBase}
          autoComplete="off"
          name="orders-search"
          placeholder="Search for orders by ID, tracking code, or external/invoice refs..."
          type="text"
          value={search}
          onChange={handleSearch}
        />
        {showRecent && (
          <div className={classes.recentlyViewedWrapper}>
            <div className={classes.recentlyViewed}>Recently viewed</div>
            <Button
              variant="ghost"
              tabIndex={-1 /* Tab straight into orders list */}
              onClick={handleClearRecentOrders}>
              Clear
            </Button>
          </div>
        )}
        {!isEmpty(orders) && (
          <List
            className={twMerge(
              classes.resultsList,
              showRecent && classes.recentList
            )}>
            {orders.map(({ id }) => (
              <SearchResult key={id} orderId={id} onSelect={handleSelect} />
            ))}
          </List>
        )}
      </div>
    </Dialog>
  );
}
